import { todayISO as melToday } from "@/lib/intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, Plus, Trash2, RefreshCw, FileSearch, ReceiptText, FileDown, Send, Stethoscope, Copy, Search, Loader2, User, AlertTriangle, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/intl";
import MembershipRenewalForm from "@/components/MembershipRenewalForm";
import { getCached, setCached } from "@/lib/pageStateCache";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

// sale_date_auto: the date was defaulted to today, so it rolls forward when the day ticks over
const defaultDraft = () => ({
  sale_id: "",
  sale_date_iso: melToday(),
  sale_date_auto: true,
  client_name: "",
  client_email: "",
  client_phone: "",
  client_address: "",
  payment_method: "Credit Card",
  provider: { id: "rafael", name: "Rafael Merino", title: "Exercise Physiologist", provider_no: "4854393B" },
  gp: { id: "", name: "", provider_no: "", clinic: "" },
  referral_date_iso: "",
  line_items: [{ description: "", qty: 1, rate: 0, gst: "GST", item_code: "" }],
  notes: "",
  source: "manual",
  source_id: "",
});

function totalsFor(items) {
  let sub = 0, gst = 0, totalInc = 0;
  for (const it of items || []) {
    const ex = Number(it.qty || 1) * Number(it.rate || 0);
    const isGst = (it.gst || "").toUpperCase() === "GST";
    sub += ex;
    if (isGst && it.total_inc != null && !Number.isNaN(Number(it.total_inc))) {
      // Authoritative gross — derive GST as the residue (avoids 1c drift).
      const lineInc = Number(it.total_inc);
      gst += (lineInc - ex);
      totalInc += lineInc;
    } else if (isGst) {
      const g = ex * 0.10;
      gst += g;
      totalInc += ex + g;
    } else {
      totalInc += ex;
    }
  }
  const r = (v) => Math.round((v + 1e-9) * 100) / 100;
  return { subtotal: r(sub), gst: r(gst), total: r(totalInc) };
}

// --- Service-date helpers (mirror backend formatting `13th Mar 2026`) -----
const _MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function _ordinalDay(d) {
  if (d >= 11 && d <= 13) return `${d}th`;
  switch (d % 10) {
    case 1: return `${d}st`;
    case 2: return `${d}nd`;
    case 3: return `${d}rd`;
    default: return `${d}th`;
  }
}
function formatServiceDate(isoDate) {
  if (!isoDate) return "";
  try {
    const [y, m, day] = isoDate.slice(0, 10).split("-").map((n) => parseInt(n, 10));
    return `${_ordinalDay(day)} ${_MONTHS_SHORT[m - 1]} ${y}`;
  } catch (_) { return isoDate; }
}
function appendDateToDescription(description, formattedDate) {
  if (!formattedDate) return description;
  const lines = (description || "").split("\n");
  const idx = lines.findIndex((ln) => ln.trim().toLowerCase().startsWith("service dates:"));
  if (idx >= 0) {
    const existing = lines[idx].replace(/^service dates:\s*/i, "").split(",").map((s) => s.trim()).filter(Boolean);
    if (existing.includes(formattedDate)) return description;
    existing.push(formattedDate);
    lines[idx] = `Service Dates: ${existing.join(", ")}`;
    return lines.join("\n");
  }
  const trimmed = (description || "").replace(/\s+$/, "");
  return (trimmed ? trimmed + "\n" : "") + `Service Dates: ${formattedDate}`;
}

export default function Receipts() {
  // Hydrate from in-memory cache so navigating to Appointments and back
  // doesn't drop the in-progress form draft or the loaded log.
  const cached = getCached("receipts");

  const [draft, setDraft] = useState(() => cached?.draft || defaultDraft());
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [providers, setProviders] = useState(cached?.providers || []);
  const [itemCodes, setItemCodes] = useState(cached?.itemCodes || []);
  const [gps, setGps] = useState(cached?.gps || []);
  const [busy, setBusy] = useState(false);
  const [lookupKind, setLookupKind] = useState(cached?.lookupKind || "appointment");
  const [lookupId, setLookupId] = useState(cached?.lookupId || "");
  const [log, setLog] = useState(cached?.log || []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagData, setDiagData] = useState(null);

  // A sale date left on its default follows the Melbourne calendar (tab open overnight, or a cached draft).
  useEffect(() => {
    const roll = () => {
      const today = melToday();
      const d = draftRef.current;
      if (!d.sale_date_auto || d.sale_date_iso === today) return;
      setDraft((x) => ({ ...x, sale_date_iso: today }));
      refreshSaleId(today);
    };
    roll();
    const id = setInterval(roll, 60000);
    window.addEventListener("focus", roll);
    document.addEventListener("visibilitychange", roll);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", roll);
      document.removeEventListener("visibilitychange", roll);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist whenever any user-meaningful state changes.
  useEffect(() => {
    setCached("receipts", {
      draft, providers, itemCodes, gps, lookupKind, lookupId, log,
      ts: Date.now(),
    });
  }, [draft, providers, itemCodes, gps, lookupKind, lookupId, log]);

  async function runDiagnose() {
    if (draft.source !== "order" || !draft.source_id) {
      toast.error("Diagnose only works on receipts created from an order.");
      return;
    }
    setDiagBusy(true);
    setDiagOpen(true);
    setDiagData(null);
    try {
      const r = await api.get(`/receipts/order-debug/${draft.source_id}`, { timeout: 60000 });
      setDiagData(r.data);
    } catch (e) {
      setDiagData({ error: e?.response?.data?.detail || e.message });
    } finally {
      setDiagBusy(false);
    }
  }

  // Bulk-apply ALL detected dates from a diagnose result. Replaces any
  // existing "Service Dates:" line in every line item. Used when the receipt
  // draft loaded with empty dates (e.g. transient Acuity error) but the
  // Diagnose endpoint successfully picked them up.
  function applyDetectedDates(byProduct) {
    if (!byProduct) return;
    const allDates = new Set();
    for (const arr of Object.values(byProduct)) {
      for (const d of arr || []) allDates.add(d);
    }
    if (allDates.size === 0) {
      toast.error("No detected dates to apply.");
      return;
    }
    const sorted = Array.from(allDates).sort();
    const formatted = sorted.map((d) => formatServiceDate(d)).join(", ");
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.map((it) => {
        const lines = (it.description || "").split("\n");
        const idx = lines.findIndex((ln) => ln.trim().toLowerCase().startsWith("service dates:"));
        if (idx >= 0) {
          lines[idx] = `Service Dates: ${formatted}`;
          return { ...it, description: lines.join("\n") };
        }
        const stripped = (it.description || "").replace(/\s+$/, "");
        const sep = stripped ? "\n" : "";
        return { ...it, description: `${stripped}${sep}Service Dates: ${formatted}` };
      }),
    }));
    toast.success(`Applied ${sorted.length} service date${sorted.length === 1 ? "" : "s"} to receipt`);
    setDiagOpen(false);
  }

  // ----- Manually add a missing service date to every line item's description.
  // Used by the Diagnose dialog's "Add" button against untagged candidates.
  function addServiceDate(isoDate) {
    if (!isoDate) return;
    const formatted = formatServiceDate(isoDate);
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.map((it) => ({
        ...it,
        description: appendDateToDescription(it.description || "", formatted),
      })),
    }));
    toast.success(`Added ${formatted} to service dates`);
  }

  useEffect(() => {
    const source = searchParams.get("source");
    const id = searchParams.get("id");
    const membershipEmail = searchParams.get("email");
    const regenSaleId = searchParams.get("regen");
    // If a URL param explicitly drives navigation, honour it (and wipe the
    // cache so the new sale_id/order takes over the form).
    if (regenSaleId) {
      loadExistingReceipt(regenSaleId);
      setSearchParams({}, { replace: true });
    } else if (source === "membership" && membershipEmail) {
      setLookupKind("membership");
      setSearchParams({}, { replace: true });
      loadInitialDraft();
    } else if ((source === "appointment" || source === "order") && id) {
      setLookupKind(source);
      setLookupId(id);
      autoFillFromParams(source, id);
      setSearchParams({}, { replace: true });
    } else if (cached?.draft) {
      // Hydrated from cache — keep the existing in-progress draft. Just
      // refresh the receipt log in the background so new receipts show up.
      loadLog();
      return;
    } else {
      loadInitialDraft();
    }
    loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadExistingReceipt(saleId) {
    setBusy(true);
    try {
      // Pull provider / item-code / GP lists first (independent of the saved doc).
      try {
        const blank = await api.get("/receipts/draft", { params: { source: "manual" } });
        setProviders(blank.data.providers || []);
        setItemCodes(blank.data.item_codes || []);
        setGps(blank.data.gps || []);
      } catch (_) { /* non-fatal — lists just stay empty */ }

      const { data } = await api.get(`/receipts/log/${encodeURIComponent(saleId)}`);
      const payload = data?.payload || {};
      setDraft({
        sale_id: payload.sale_id || saleId,
        sale_date_iso: payload.sale_date_iso || melToday(),
        client_name: payload.client_name || "",
        client_email: payload.client_email || "",
        client_phone: payload.client_phone || "",
        client_address: payload.client_address || "",
        payment_method: payload.payment_method || "Credit Card",
        provider: payload.provider || defaultDraft().provider,
        gp: payload.gp || { id: "", name: "", provider_no: "", clinic: "" },
        referral_date_iso: payload.referral_date_iso || "",
        line_items: (payload.line_items && payload.line_items.length)
          ? payload.line_items
          : defaultDraft().line_items,
        notes: payload.notes || "",
        source: payload.source || "manual",
        source_id: payload.source_id || "",
      });
      toast.success(`Loaded receipt ${saleId}`, {
        description: `Generated ${data?.generated_at?.slice(0, 10) || ""}${data?.emailed_to ? ` · emailed to ${data.emailed_to}` : ""}`,
      });
    } catch (e) {
      const detail = e?.response?.status === 404
        ? `Receipt ${saleId} no longer exists in the log.`
        : (e?.response?.data?.detail || e.message || "Could not load receipt");
      toast.error(detail);
      loadInitialDraft();
    } finally {
      setBusy(false);
    }
  }

  // Capture-once initial values for the membership form (from URL).
  const [initialMembershipEmail, setInitialMembershipEmail] = useState(
    () => searchParams.get("email") || "");
  const [initialMembershipChargeId] = useState(() => searchParams.get("charge_id") || "");
  const [initialMembershipCertCode, setInitialMembershipCertCode] = useState("");
  const [initialMembershipBaseDesc, setInitialMembershipBaseDesc] = useState("");

  // When the backend flags an order as belonging to a subscription
  // (product name matches "membership" / "subscription" / a
  // frequency suffix like "/wk"), we surface a banner offering a
  // one-click switch to the Stripe-backed renewal flow. Acuity's
  // /orders endpoint only exposes the FIRST payment of a
  // subscription, so an order-based receipt for a fortnightly
  // membership would perpetually show only $120 no matter which
  // renewal period the reviewer is chasing.
  const [subscriptionHint, setSubscriptionHint] = useState(null);

  function switchToMembershipFlow() {
    if (!subscriptionHint) return;
    setInitialMembershipEmail(subscriptionHint.email || "");
    setInitialMembershipCertCode(subscriptionHint.certificate_code || "");
    setInitialMembershipBaseDesc(subscriptionHint.product_name || "");
    setLookupKind("membership");
    setSubscriptionHint(null);
    toast.info("Switched to Membership renewal (Stripe)", {
      description: "Pick the specific renewal charge to receipt.",
      duration: 6000,
    });
  }

  async function bulkGenerateFromHint() {
    if (!subscriptionHint) return;
    if (!subscriptionHint.certificate_code) {
      toast.error("No certificate code detected — switch to the Membership Renewal flow first and paste it.");
      switchToMembershipFlow();
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        "/receipts/membership/bulk-generate",
        {
          email: subscriptionHint.email,
          certificate_code: subscriptionHint.certificate_code,
          base_description: subscriptionHint.product_name || "Membership renewal",
        },
        { timeout: 180000 },
      );
      const s = r.data.summary || {};
      toast.success(
        `Created ${s.created} · skipped ${s.skipped} · failed ${s.failed}`,
        {
          description:
            "Open the receipts log below to download each renewal.",
          duration: 10000,
        },
      );
      // Refresh the log so the new receipts appear
      await loadLog();
      // Also hop the user into the membership flow so they can see
      // the detail table + regenerate any that failed.
      switchToMembershipFlow();
    } catch (e) {
      toast.error("Bulk generate failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBusy(false); }
  }

  async function autoFillFromParams(source, id) {
    setBusy(true);
    try {
      const { data } = await api.get("/receipts/draft", {
        params: {
          source,
          appointment_id: source === "appointment" ? id : undefined,
          order_id: source === "order" ? id : undefined,
        },
      });
      hydrateDraft(data);
      toast.success(`Auto-filled from ${source} ${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fill failed — starting blank draft");
      loadInitialDraft();
    } finally { setBusy(false); }
  }

  async function loadInitialDraft() {
    try {
      const { data } = await api.get("/receipts/draft", { params: { source: "manual" } });
      hydrateDraft(data);
    } catch (e) {
      toast.error("Could not load receipt draft");
    }
  }

  async function loadLog() {
    try {
      const { data } = await api.get("/receipts/log", { params: { limit: 25 } });
      setLog(data || []);
    } catch (e) { /* ignore */ }
  }

  async function regenReceipt(saleId, fmt) {
    try {
      const res = await api.post(
        `/receipts/regenerate/${encodeURIComponent(saleId)}`,
        null,
        { params: { format: fmt }, responseType: "blob", timeout: 90000 },
      );
      const blob = new Blob([res.data], {
        type: fmt === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${saleId}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Re-downloaded ${saleId} as ${fmt.toUpperCase()}`);
    } catch (e) {
      let detail = e?.message;
      // Blob error body needs special handling
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          detail = JSON.parse(text)?.detail || text;
        } catch { /* ignore */ }
      } else {
        detail = e?.response?.data?.detail || e?.message;
      }
      toast.error("Re-download failed", { description: detail });
    }
  }

  function hydrateDraft(data) {
    setProviders(data.providers || []);
    setItemCodes(data.item_codes || []);
    setGps(data.gps || []);
    setDraft((prev) => ({
      sale_id: data.sale_id,
      sale_date_iso: data.sale_date_iso,
      sale_date_auto: data.source === "manual",
      client_name: data.client_name || "",
      client_email: data.client_email || "",
      client_phone: data.client_phone || "",
      client_address: data.client_address || "",
      payment_method: data.payment_method || "Credit Card",
      provider: data.provider || defaultDraft().provider,
      // GP + referral are not in Acuity — preserve any user-selected GP
      // across auto-fills (don't blow it away when fetching a fresh draft).
      gp: data.gp || prev.gp || { id: "", name: "", provider_no: "", clinic: "" },
      referral_date_iso: data.referral_date_iso ?? prev.referral_date_iso ?? "",
      line_items: (data.line_items && data.line_items.length)
        ? data.line_items
        : defaultDraft().line_items,
      notes: data.notes || "",
      source: data.source || "manual",
      source_id: data.source_id || "",
    }));
    // Capture / clear the subscription hint so the banner appears
    // after an order lookup that matches a subscription product.
    setSubscriptionHint(data.subscription_hint || null);
    // If this was an order-sourced draft but no service-dates landed on any
    // line item, warn the user — almost always a transient Acuity hiccup.
    if (data.source === "order") {
      const hasDates = (data.line_items || []).some((it) =>
        /service\s*dates?\s*:/i.test(it.description || ""),
      );
      const dbg = data._service_dates_debug || {};
      if (!hasDates) {
        if (dbg.error) {
          toast.warning("Couldn't auto-detect service dates", {
            description: `${dbg.error}. Click Diagnose to retry, then "Apply to receipt".`,
            duration: 10000,
          });
        } else {
          toast.warning("No service dates were auto-detected", {
            description: 'Click "Diagnose" to retry — most often a transient Acuity error.',
            duration: 10000,
          });
        }
      }
    }
  }

  async function refreshSaleId(dateIso) {
    try {
      const { data } = await api.get("/receipts/next-sale-id", { params: { date: dateIso } });
      setDraft((d) => ({ ...d, sale_id: data.sale_id }));
    } catch (e) { /* ignore */ }
  }

  async function autofill() {
    if (!lookupId.trim()) {
      toast.error("Enter an ID first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/receipts/draft", {
        params: {
          source: lookupKind,
          appointment_id: lookupKind === "appointment" ? lookupId.trim() : undefined,
          order_id: lookupKind === "order" ? lookupId.trim() : undefined,
        },
      });
      hydrateDraft(data);
      toast.success(`Auto-filled from ${lookupKind} ${lookupId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fill failed");
    } finally { setBusy(false); }
  }

  function updateLine(idx, patch) {
    // Clear total_inc if the user manually edits qty / rate / gst — the
    // captured gross from auto-fill no longer reflects the new line. We keep
    // it when only description / item_code change.
    const invalidatesInc = (
      Object.prototype.hasOwnProperty.call(patch, "qty") ||
      Object.prototype.hasOwnProperty.call(patch, "rate") ||
      Object.prototype.hasOwnProperty.call(patch, "gst")
    );
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, ...patch };
        if (invalidatesInc) delete next.total_inc;
        return next;
      }),
    }));
  }
  function addLine() {
    setDraft((d) => ({
      ...d,
      line_items: [...d.line_items, { description: "", qty: 1, rate: 0, gst: "GST", item_code: "" }],
    }));
  }
  function removeLine(idx) {
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.length > 1 ? d.line_items.filter((_, i) => i !== idx) : d.line_items,
    }));
  }

  function applyItemCodePreset(idx, code) {
    const preset = itemCodes.find((c) => c.code === code);
    if (!preset) return;
    setDraft((d) => ({
      ...d,
      line_items: d.line_items.map((it, i) => {
        if (i !== idx) return it;
        const current = it.description || "";
        const lines = current.split("\n");
        const datesIdx = lines.findIndex((ln) => ln.trim().toLowerCase().startsWith("service dates:"));
        const above = datesIdx >= 0 ? lines.slice(0, datesIdx) : [...lines];
        const datesBlock = datesIdx >= 0 ? lines.slice(datesIdx) : [];

        // If the user previously picked an item-code preset, strip its
        // description from `above` so swapping presets doesn't stack
        // multiple item-code blurbs above the service-dates line.
        let aboveCleaned = [...above];
        const prevPreset = it.item_code
          ? itemCodes.find((c) => c.code === it.item_code)
          : null;
        if (prevPreset && prevPreset.description) {
          const prevLines = prevPreset.description.split("\n");
          const N = prevLines.length;
          // Search for the previous preset block as a contiguous subsequence.
          for (let start = aboveCleaned.length - N; start >= 0; start--) {
            let match = true;
            for (let k = 0; k < N; k++) {
              if (aboveCleaned[start + k] !== prevLines[k]) { match = false; break; }
            }
            if (match) {
              aboveCleaned.splice(start, N);
              break;
            }
          }
        }
        // Trim trailing blank lines from the original-description block.
        while (aboveCleaned.length && aboveCleaned[aboveCleaned.length - 1].trim() === "") {
          aboveCleaned.pop();
        }

        const presetLines = preset.description.split("\n");
        // Final layout: [original order description] · [preset description] · [service dates]
        const newLines = [
          ...aboveCleaned,
          ...presetLines,
          ...datesBlock,
        ];
        // Drop any leading blank lines.
        while (newLines.length && newLines[0].trim() === "") newLines.shift();
        return { ...it, item_code: code, description: newLines.join("\n") };
      }),
    }));
  }

  async function generate(format = "docx") {
    if (!draft.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    if (!draft.line_items.some((it) => Number(it.rate) > 0)) {
      toast.error("At least one line item with a rate is required");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        `/receipts/generate?format=${format}`,
        draft,
        { responseType: "blob" },
      );
      const blob = res.data;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `receipt_${draft.sale_id}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const noun = format === "pdf" ? "PDF" : "Word";
      toast.success(`Receipt ${draft.sale_id} downloaded as ${noun}`);
      loadLog();
      refreshSaleId(draft.sale_date_iso);
    } catch (e) {
      // axios error with responseType=blob — error body is a Blob, need to read it.
      let detail = e?.message || "Generate failed";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          detail = json.detail || text || detail;
        } catch (_) { /* keep default */ }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(detail);
    } finally { setBusy(false); }
  }

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: "",
    cc: "",
    subject: "",
    message: "",
    attach_format: "pdf",
  });
  // Aliases pulled from the client's profile on dialog open — the
  // backend auto-CCs them, we surface them in the preview so the
  // operator sees every inbox before hitting Send.
  const [profileAliases, setProfileAliases] = useState([]);

  function openEmailDialog() {
    if (!draft.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }
    if (!draft.line_items.some((it) => Number(it.rate) > 0)) {
      toast.error("At least one line item with a rate is required");
      return;
    }
    setEmailForm({
      to: draft.client_email || "",
      cc: "",
      subject: `Tax receipt · ${draft.sale_id || ""} · ${draft.client_name || ""}`.replace(/\s+·\s*$/g, "").trim(),
      message: `Hi ${(draft.client_name || "").split(" ")[0] || "there"},\n\nPlease find your tax receipt attached. Let us know if you need anything else.\n\nKind regards,\nLiving Energy Pilates & Rehab`,
      attach_format: "pdf",
    });
    setEmailDialogOpen(true);
    // Load profile aliases so the Recipients preview shows the same
    // CC list the backend will build. Best-effort — silent on 404.
    const em = (draft.client_email || "").trim();
    setProfileAliases([]);
    if (em) {
      api.get(`/clients/${encodeURIComponent(em)}/profile`)
        .then((r) => setProfileAliases(r.data?.profile?.additional_emails || []))
        .catch(() => setProfileAliases([]));
    }
  }

  async function sendEmail() {
    if (!emailForm.to.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    setBusy(true);
    try {
      const body = { ...draft, ...emailForm };
      // Propagate the workspace's canonical scope when the Receipts
      // page was opened from a scoped ``/clients/<email>?as=<cid>``
      // trail — so the outbound receipt auto-CCs the correct sibling
      // on a shared-email household (Rosemarie / Judy). No-op when
      // the URL doesn't carry the param (legacy invocations).
      const scopedCid = searchParams.get("canonical_client_id");
      if (scopedCid) body.canonical_client_id = scopedCid;
      // Defensive link preservation. If the draft lost its
      // appointment/order lineage (observed on cold-cache navigation
      // between Appointments and Receipts, particularly when Email is
      // clicked without Download-first), fall back to the lookup
      // context that was captured from the URL when the page opened.
      // ``lookupKind`` / ``lookupId`` are cached and survive re-renders
      // that the ``draft`` state doesn't, so they're the most reliable
      // source of truth for "this receipt is for this appointment".
      const hasSourceLink = body.source && body.source !== "manual"
        && String(body.source_id || "").trim();
      if (!hasSourceLink
          && (lookupKind === "appointment" || lookupKind === "order")
          && String(lookupId || "").trim()) {
        body.source = lookupKind;
        body.source_id = String(lookupId).trim();
      }
      const r = await api.post(`/receipts/email`, body);
      const linkedSaleId = r?.data?.sale_id || draft.sale_id;
      toast.success(`Receipt ${linkedSaleId} emailed to ${emailForm.to}`);
      setEmailDialogOpen(false);
      // Verify the receipt actually linked back to the appointment/order.
      // If not, tell the operator so they can regenerate rather than
      // silently ending up with an orphaned receipt.
      if (body.source && body.source_id && linkedSaleId) {
        try {
          const check = await api.get(
            `/receipts/log/${encodeURIComponent(linkedSaleId)}`,
          );
          const p = check?.data?.payload || {};
          if (!p.source_id || String(p.source_id) !== String(body.source_id)) {
            toast.warning("Receipt sent, but link to the appointment "
                            + "wasn't saved. Please regenerate from the "
                            + "appointment to re-link.");
          }
        } catch { /* non-fatal */ }
      }
      loadLog();
      refreshSaleId(draft.sale_date_iso);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Email failed");
    } finally { setBusy(false); }
  }

  const totals = useMemo(() => totalsFor(draft.line_items), [draft.line_items]);

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
            <ReceiptText className="h-8 w-8" /> Receipts
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Generate editable Word receipts for private health, NDIS & Medicare claims.
          </p>
        </div>
      </div>

      {/* Auto-fill from Acuity */}
      <Card className="p-5 border-zinc-200">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-950 mb-3">
          <FileSearch className="h-4 w-4" /> Auto-fill from Acuity
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Source</Label>
            <Select value={lookupKind} onValueChange={setLookupKind}>
              <SelectTrigger className="w-56" data-testid="receipts-lookup-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="appointment">Appointment (single)</SelectItem>
                <SelectItem value="order">Order (block of sessions)</SelectItem>
                <SelectItem value="membership">Membership renewal (Stripe)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {lookupKind !== "membership" && (
            <>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs">{lookupKind === "appointment" ? "Appointment ID" : "Order ID"}</Label>
                <Input
                  value={lookupId}
                  onChange={(e) => setLookupId(e.target.value)}
                  placeholder={lookupKind === "appointment" ? "e.g. 1567890123" : "e.g. 162685906"}
                  data-testid="receipts-lookup-id"
                />
              </div>
              <Button onClick={autofill} disabled={busy} data-testid="receipts-autofill-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Fetch & Fill
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={loadInitialDraft} disabled={busy} data-testid="receipts-reset-btn">
            Reset to blank
          </Button>
        </div>
        {lookupKind !== "membership" ? (
          <p className="text-xs text-zinc-500 mt-2">
            Find the Appointment ID on the Appointments page row (or Order ID on Orders). Everything fetched is
            editable in the form below before generating.
          </p>
        ) : (
          <MembershipRenewalForm
            itemCodes={itemCodes}
            onDraft={(data) => { hydrateDraft(data); }}
            initialEmail={initialMembershipEmail}
            initialChargeId={initialMembershipChargeId}
            initialCertCode={initialMembershipCertCode}
            initialBaseDesc={initialMembershipBaseDesc}
          />
        )}
      </Card>

      {/* Subscription-order hand-off banner. Fires when the reviewer
          auto-filled from an order whose product looks like a
          membership/subscription (see backend
          `_detect_subscription_hint`). The order-based flow can only
          ever surface the FIRST payment, so we nudge them toward the
          Stripe-backed renewal flow with the certificate code and
          email already pre-filled. */}
      {subscriptionHint && lookupKind !== "membership" && (
        <Card className="p-4 border-amber-300 bg-amber-50"
              data-testid="subscription-hint-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                This order is a subscription — only the first payment is shown.
              </div>
              <div className="text-xs text-amber-800 mt-1">
                <span className="font-medium">{subscriptionHint.product_name}</span>
                {subscriptionHint.email && (
                  <> · {subscriptionHint.email}</>
                )}
                {subscriptionHint.certificate_code && (
                  <> · certificate <span className="font-mono">{subscriptionHint.certificate_code}</span></>
                )}
              </div>
              <div className="text-xs text-amber-800 mt-1">
                {subscriptionHint.reason}
              </div>
            </div>
            <Button
              size="sm"
              onClick={switchToMembershipFlow}
              className="bg-amber-700 hover:bg-amber-800 text-white shrink-0"
              data-testid="switch-to-membership-flow"
            >
              Switch to Membership Renewal
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          {subscriptionHint.certificate_code && (
            <div className="mt-2 pl-8 flex items-center gap-2">
              <span className="text-2xs text-amber-800">
                Or skip straight to the outcome →
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={bulkGenerateFromHint}
                disabled={busy}
                className="border-amber-400 bg-white text-amber-900 hover:bg-amber-100 h-7"
                data-testid="bulk-generate-from-hint"
              >
                <Zap className="h-3.5 w-3.5 mr-1" />
                Generate ALL renewals now
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Receipt form */}
      <Card className="p-5 border-zinc-200 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Sale date</Label>
            <Input
              type="date"
              value={draft.sale_date_iso}
              onChange={(e) => {
                const v = e.target.value;
                setDraft((d) => ({ ...d, sale_date_iso: v, sale_date_auto: false }));
                refreshSaleId(v);
              }}
              data-testid="receipts-sale-date"
            />
          </div>
          <div>
            <Label>Sale ID</Label>
            <Input
              value={draft.sale_id}
              onChange={(e) => setDraft((d) => ({ ...d, sale_id: e.target.value }))}
              data-testid="receipts-sale-id"
            />
          </div>
          <div>
            <Label>Provider</Label>
            <Select
              value={draft.provider?.id || ""}
              onValueChange={(id) => {
                const p = providers.find((x) => x.id === id);
                if (p) setDraft((d) => ({ ...d, provider: p }));
              }}
            >
              <SelectTrigger data-testid="receipts-provider">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} — {p.provider_no}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Client name</Label>
            <ReceiptClientPicker
              name={draft.client_name}
              onNameChange={(v) => setDraft((d) => ({ ...d, client_name: v }))}
              onPick={(c) => {
                setDraft((d) => {
                  // Prefill line item with the client's most-recent appt type,
                  // but ONLY overwrite the first line when it's still an empty
                  // default row — don't stomp on values the user has typed.
                  let next = {
                    ...d,
                    client_name: c.name || d.client_name,
                    client_email: c.email || d.client_email,
                    client_phone: c.phone || d.client_phone,
                  };
                  const line = c.suggested_line;
                  if (line && d.line_items.length) {
                    const first = d.line_items[0];
                    const firstIsEmpty =
                      !(first.description || "").trim() && !Number(first.rate);
                    if (firstIsEmpty) {
                      const items = [...d.line_items];
                      items[0] = {
                        ...first,
                        description: line.description,
                        rate: Number(line.rate) || 0,
                        qty: line.qty || 1,
                        gst: line.gst || "GST",
                        item_code: first.item_code || "",
                      };
                      next.line_items = items;
                    }
                  }
                  return next;
                });
                if (c.suggested_line?.has_price === false && c.last_appointment_type) {
                  toast.info(`Filled line with "${c.last_appointment_type}"`, {
                    description: "No price on file — please set the rate manually.",
                  });
                } else if (c.suggested_line) {
                  toast.success(`Prefilled from last appointment: ${c.suggested_line.description}`);
                }
              }}
            />
          </div>
          <div>
            <Label>Payment method</Label>
            <Input
              value={draft.payment_method}
              onChange={(e) => setDraft((d) => ({ ...d, payment_method: e.target.value }))}
              data-testid="receipts-payment-method"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Client email (optional)</Label>
            <Input
              value={draft.client_email}
              onChange={(e) => setDraft((d) => ({ ...d, client_email: e.target.value }))}
              data-testid="receipts-client-email"
            />
          </div>
          <div>
            <Label>Client phone (optional)</Label>
            <Input
              value={draft.client_phone}
              onChange={(e) => setDraft((d) => ({ ...d, client_phone: e.target.value }))}
              data-testid="receipts-client-phone"
            />
          </div>
        </div>

        <div>
          <Label>Client address (optional)</Label>
          <Textarea
            rows={2}
            value={draft.client_address}
            onChange={(e) => setDraft((d) => ({ ...d, client_address: e.target.value }))}
            data-testid="receipts-client-address"
          />
        </div>

        {/* Referring GP (for Medicare claiming) */}
        <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50/60 p-4" data-testid="receipts-gp-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <Label className="text-sm font-semibold text-zinc-950">Referring GP (optional — for Medicare claiming)</Label>
              <p className="text-xs text-zinc-500 mt-0.5">
                Pick a saved GP to auto-fill, or type details below. Saved GPs are managed in Settings → Tax Receipts.
              </p>
            </div>
            {(draft.gp?.name || draft.gp?.provider_no || draft.gp?.clinic || draft.referral_date_iso) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDraft((d) => ({
                  ...d,
                  gp: { id: "", name: "", provider_no: "", clinic: "" },
                  referral_date_iso: "",
                }))}
                data-testid="receipts-gp-clear"
                className="text-xs text-zinc-500 hover:text-zinc-900"
              >
                Clear
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label className="text-xs">Saved GP</Label>
              <Select
                value={draft.gp?.id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    setDraft((d) => ({ ...d, gp: { id: "", name: "", provider_no: "", clinic: "" } }));
                    return;
                  }
                  const picked = gps.find((g) => g.id === v);
                  if (picked) {
                    setDraft((d) => ({
                      ...d,
                      gp: {
                        id: picked.id,
                        name: picked.name || "",
                        provider_no: picked.provider_no || "",
                        clinic: picked.clinic || "",
                      },
                    }));
                  }
                }}
              >
                <SelectTrigger data-testid="receipts-gp-picker">
                  <SelectValue placeholder={gps.length ? "— Pick a saved GP —" : "No GPs saved yet"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None / clear —</SelectItem>
                  {gps.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name || g.id}{g.clinic ? ` · ${g.clinic}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Referral date</Label>
              <Input
                type="date"
                value={draft.referral_date_iso || ""}
                onChange={(e) => setDraft((d) => ({ ...d, referral_date_iso: e.target.value }))}
                data-testid="receipts-referral-date"
              />
            </div>
            <div className="md:col-span-5" />

            <div className="md:col-span-4">
              <Label className="text-xs">GP name</Label>
              <Input
                value={draft.gp?.name || ""}
                onChange={(e) => setDraft((d) => ({ ...d, gp: { ...(d.gp || {}), name: e.target.value, id: "" } }))}
                placeholder="Dr Rachel De Jong"
                data-testid="receipts-gp-name"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">GP Provider No.</Label>
              <Input
                value={draft.gp?.provider_no || ""}
                onChange={(e) => setDraft((d) => ({ ...d, gp: { ...(d.gp || {}), provider_no: e.target.value, id: "" } }))}
                placeholder="1864942B"
                className="font-mono-num"
                data-testid="receipts-gp-provider-no"
              />
            </div>
            <div className="md:col-span-5">
              <Label className="text-xs">Clinic</Label>
              <Input
                value={draft.gp?.clinic || ""}
                onChange={(e) => setDraft((d) => ({ ...d, gp: { ...(d.gp || {}), clinic: e.target.value, id: "" } }))}
                placeholder="Andrew Place Clinic"
                data-testid="receipts-gp-clinic"
              />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Line items</Label>
            <Button variant="outline" size="sm" onClick={addLine} data-testid="receipts-add-line">
              <Plus className="h-4 w-4 mr-1" /> Add line
            </Button>
          </div>
          <div className="overflow-x-auto rounded-md border border-zinc-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Description</TableHead>
                  <TableHead className="w-[14%]">Item code preset</TableHead>
                  <TableHead className="w-[8%] text-right">Qty</TableHead>
                  <TableHead className="w-[12%] text-right">Rate ex-GST</TableHead>
                  <TableHead className="w-[10%]">Tax</TableHead>
                  <TableHead className="w-[12%] text-right">Line total</TableHead>
                  <TableHead className="w-[4%]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.line_items.map((it, idx) => {
                  const ex = Number(it.qty || 1) * Number(it.rate || 0);
                  const gstAmt = (it.gst || "").toUpperCase() === "GST" ? ex * 0.10 : 0;
                  return (
                    <TableRow key={idx} data-testid={`receipts-line-${idx}`}>
                      <TableCell>
                        <Textarea
                          rows={3}
                          value={it.description}
                          onChange={(e) => updateLine(idx, { description: e.target.value })}
                          placeholder="e.g. Exercise Physiology Initial Consultation (Item 10953)..."
                          data-testid={`receipts-line-desc-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={it.item_code || ""}
                          onValueChange={(v) => applyItemCodePreset(idx, v)}
                        >
                          <SelectTrigger data-testid={`receipts-line-preset-${idx}`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            {itemCodes.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.code} — {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="1" min="1"
                          value={it.qty}
                          onChange={(e) => updateLine(idx, { qty: e.target.value })}
                          className="text-right font-mono-num"
                          data-testid={`receipts-line-qty-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01"
                          value={it.rate}
                          onChange={(e) => updateLine(idx, { rate: e.target.value })}
                          className="text-right font-mono-num"
                          data-testid={`receipts-line-rate-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select value={it.gst || "GST"} onValueChange={(v) => updateLine(idx, { gst: v })}>
                          <SelectTrigger data-testid={`receipts-line-gst-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GST">GST (10%)</SelectItem>
                            <SelectItem value="FRE">GST-Free</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right font-mono-num">
                        {fmtMoney(ex + gstAmt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLine(idx)}
                          disabled={draft.line_items.length <= 1}
                          data-testid={`receipts-line-remove-${idx}`}
                        >
                          <Trash2 className="h-4 w-4 text-zinc-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex flex-col items-end gap-1 pr-2 text-sm" data-testid="receipts-totals">
          <div className="flex gap-8"><span className="text-zinc-500">Subtotal (ex-GST)</span><span className="font-mono-num w-28 text-right">{fmtMoney(totals.subtotal)}</span></div>
          <div className="flex gap-8"><span className="text-zinc-500">GST</span><span className="font-mono-num w-28 text-right">{fmtMoney(totals.gst)}</span></div>
          <div className="flex gap-8 text-base font-semibold"><span>Total paid</span><span className="font-mono-num w-28 text-right">{fmtMoney(totals.total)}</span></div>
        </div>

        <div>
          <Label>Footer note (optional)</Label>
          <Textarea
            rows={2}
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            placeholder="e.g. This receipt may be used for private health fund / Medicare / NDIS claiming."
            data-testid="receipts-notes"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
          {draft.source === "order" && draft.source_id && (
            <Button
              onClick={runDiagnose}
              disabled={busy || diagBusy}
              size="lg"
              variant="outline"
              data-testid="receipts-diagnose-btn"
              title="Show how service dates were detected for this order"
            >
              <Stethoscope className="h-4 w-4 mr-2" /> Diagnose
            </Button>
          )}
          <Button
            onClick={openEmailDialog}
            disabled={busy}
            size="lg"
            variant="outline"
            data-testid="receipts-email-btn"
          >
            <Send className="h-4 w-4 mr-2" /> Email to client
          </Button>
          <Button
            onClick={() => generate("pdf")}
            disabled={busy}
            size="lg"
            variant="outline"
            data-testid="receipts-generate-pdf-btn"
          >
            <FileDown className="h-4 w-4 mr-2" /> Download PDF
          </Button>
          <Button
            onClick={() => generate("docx")}
            disabled={busy}
            size="lg"
            data-testid="receipts-generate-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Download .docx
          </Button>
        </div>
      </Card>

      {/* Diagnose dialog */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="receipts-diagnose-dialog">
          <DialogHeader>
            <DialogTitle>Service-date diagnosis</DialogTitle>
          </DialogHeader>
          {diagBusy ? (
            <div className="py-8 text-sm text-zinc-500">Querying Acuity… can take 20–40 seconds.</div>
          ) : !diagData ? (
            <div className="py-8 text-sm text-zinc-500">No data.</div>
          ) : (
            <div className="space-y-4 text-sm">
              <DiagSummary data={diagData} onAddDate={addServiceDate} onApplyAll={applyDetectedDates} />
              <details className="rounded-md border border-zinc-200 bg-zinc-50 p-2">
                <summary className="cursor-pointer text-zinc-700 px-2 py-1">Raw JSON</summary>
                <pre className="mt-2 max-h-[400px] overflow-auto text-2xs font-mono-num text-zinc-700 whitespace-pre-wrap break-all px-2">
                  {JSON.stringify(diagData, null, 2)}
                </pre>
              </details>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(diagData, null, 2));
                    toast.success("Diagnostic JSON copied");
                  }}
                  data-testid="receipts-diagnose-copy"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy JSON
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="receipts-email-dialog">
          <DialogHeader>
            <DialogTitle>Email receipt {draft.sale_id} to client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To</Label>
              <Input
                type="email"
                value={emailForm.to}
                onChange={(e) => setEmailForm((f) => ({ ...f, to: e.target.value }))}
                placeholder="client@example.com"
                data-testid="receipts-email-to"
              />
            </div>
            <div>
              <Label>CC (optional, comma-separated)</Label>
              <Input
                value={emailForm.cc}
                onChange={(e) => setEmailForm((f) => ({ ...f, cc: e.target.value }))}
                placeholder="copy@example.com"
                data-testid="receipts-email-cc"
              />
            </div>
            <div>
              <Label>Subject (optional)</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder={`Tax Receipt ${draft.sale_id} — Living Energy Pilates & Rehab`}
                data-testid="receipts-email-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={6}
                value={emailForm.message}
                onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                data-testid="receipts-email-message"
              />
            </div>
            <div>
              <Label>Attach as</Label>
              <Select
                value={emailForm.attach_format}
                onValueChange={(v) => setEmailForm((f) => ({ ...f, attach_format: v }))}
              >
                <SelectTrigger data-testid="receipts-email-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="docx">Word .docx</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Recipients preview — mirrors exactly what the backend
                will build so the operator sees every inbox (primary
                To + profile aliases + operator CC) before hitting
                Send. Important for two-parent-child receipts where
                the aliases are otherwise invisible. */}
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5"
                 data-testid="receipts-recipients-preview">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium mb-1.5">
                Will be sent to
              </div>
              <ul className="space-y-1 text-[0.75rem]">
                <li className="flex items-baseline gap-2"
                    data-testid="receipts-preview-to">
                  <span className="w-8 shrink-0 text-2xs uppercase text-zinc-500 font-medium">To</span>
                  <span className="font-mono text-zinc-800 break-all">
                    {emailForm.to || <span className="italic text-zinc-400">no recipient</span>}
                  </span>
                </li>
                {profileAliases.map((em) => (
                  <li key={`ra-${em}`}
                      className="flex items-baseline gap-2"
                      data-testid={`receipts-preview-alias-${em}`}>
                    <span className="w-8 shrink-0 text-2xs uppercase text-zinc-500 font-medium">Cc</span>
                    <span className="font-mono text-zinc-800 break-all">{em}</span>
                    <span className="text-2xs text-zinc-500">alias on profile</span>
                  </li>
                ))}
                {emailForm.cc.split(/[\s,;]+/)
                  .map((s) => s.trim())
                  .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s))
                  .map((em) => (
                    <li key={`rc-${em}`}
                        className="flex items-baseline gap-2"
                        data-testid={`receipts-preview-cc-${em}`}>
                      <span className="w-8 shrink-0 text-2xs uppercase text-zinc-500 font-medium">Cc</span>
                      <span className="font-mono text-zinc-800 break-all">{em}</span>
                      <span className="text-2xs text-zinc-500">extra CC</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailDialogOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={sendEmail} disabled={busy} data-testid="receipts-email-send">
              <Send className="h-4 w-4 mr-2" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recent receipts log */}
      <Card className="p-5 border-zinc-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-zinc-950">Recent receipts</h2>
          <Button variant="ghost" size="sm" onClick={loadLog} data-testid="receipts-log-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
        {log.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4">No receipts generated yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sale ID</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Emailed</TableHead>
                <TableHead>Generated</TableHead>
                <TableHead className="text-right w-32">Re-download</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.map((r, idx) => (
                <TableRow key={r.sale_id || `row-${idx}`} data-testid={`receipts-log-row-${r.sale_id || idx}`}>
                  <TableCell className="font-mono-num">{r.sale_id}</TableCell>
                  <TableCell>{r.client_name}</TableCell>
                  <TableCell>{r.provider_id}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtMoney(r.total)}</TableCell>
                  <TableCell><Badge variant="outline">{r.source || "manual"}</Badge></TableCell>
                  <TableCell className="text-xs text-zinc-500">
                    {r.emailed_to ? (
                      <span title={`${r.emailed_format?.toUpperCase()} on ${r.emailed_at}`}>
                        <Send className="h-3 w-3 inline mr-1 text-emerald-600" />
                        {r.emailed_to}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">{r.generated_at?.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => regenReceipt(r.sale_id, "pdf")}
                        data-testid={`receipts-log-pdf-${r.sale_id}`}
                        title="Re-download PDF"
                      >
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => regenReceipt(r.sale_id, "docx")}
                        data-testid={`receipts-log-docx-${r.sale_id}`}
                        title="Re-download DOCX"
                      >
                        DOCX
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}


function DiagSummary({ data, onAddDate, onApplyAll }) {
  if (data?.error) {
    return (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">
        {data.error}
      </div>
    );
  }
  const debug = data?.debug || {};
  const order = data?.order || {};
  const byProduct = data?.by_product || {};
  const products = order.products || [];
  const certRedemptions = debug.cert_redemptions || {};
  const strategy = debug.strategy_used || {};
  const caps = debug.caps || {};
  const certificateCodesByProduct = debug.certificate_codes_by_product || {};
  const untaggedCandidates = debug.untagged_candidates || {};
  const totalDates = Object.values(byProduct).reduce((n, arr) => n + (arr?.length || 0), 0);

  return (
    <div className="space-y-4">
      {totalDates > 0 && onApplyAll && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 flex items-start justify-between gap-3">
          <div className="text-sm text-emerald-900">
            <strong>{totalDates}</strong> service date{totalDates === 1 ? "" : "s"} detected for this order.
            If the receipt didn't load them, click → Apply.
          </div>
          <Button
            size="sm"
            onClick={() => onApplyAll(byProduct)}
            data-testid="diag-apply-all-dates"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            Apply to receipt
          </Button>
        </div>
      )}

      <div className="rounded-md border border-zinc-200 p-3 bg-white">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Order context</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div className="text-zinc-500">Order ID</div><div className="font-mono-num">{order.id}</div>
          <div className="text-zinc-500">Order date</div><div className="font-mono-num">{order.time}</div>
          <div className="text-zinc-500">Email</div><div className="font-mono-num">{order.email}</div>
          <div className="text-zinc-500">Email-window appts fetched</div>
          <div className="font-mono-num">{debug.email_appts_count ?? "—"}</div>
        </div>
      </div>

      {products.length > 0 && (
        <div className="space-y-3">
          {products.map((p) => {
            const dates = byProduct[String(p.id)] || [];
            const codes = certificateCodesByProduct[String(p.id)] || [];
            const candidates = untaggedCandidates[String(p.id)] || [];
            const cap = caps[String(p.id)] || {};
            return (
              <div key={p.id} className="rounded-md border border-zinc-200 p-3 bg-white">
                <div className="font-medium text-zinc-950">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  qty {p.quantity} · ${p.total} · product #{p.id}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="text-zinc-500">Strategy used</div>
                  <div className="font-mono-num">{strategy[String(p.id)] || "none"}</div>
                  <div className="text-zinc-500">Certificate code(s)</div>
                  <div className="font-mono-num">{codes.join(", ") || "—"}</div>
                  <div className="text-zinc-500">Appointments matched by cert</div>
                  <div className="font-mono-num">{cap.matched_by_cert ?? "—"}</div>
                  <div className="text-zinc-500">Pack size (from name)</div>
                  <div className="font-mono-num">{cap.pack_size_from_name || "—"}</div>
                </div>
                {codes.map((c) => (
                  <div key={c} className="mt-1 text-xs text-zinc-600">
                    Cert <span className="font-mono-num">{c}</span>: matched{" "}
                    <span className="font-mono-num">{certRedemptions[c] ?? 0}</span> appointment(s) in email window
                  </div>
                ))}
                <div className="mt-2 text-xs text-zinc-500">Dates picked ({dates.length}):</div>
                <div className="font-mono-num text-xs text-zinc-700">
                  {dates.length ? dates.join(", ") : "—"}
                </div>

                {candidates.length > 0 && onAddDate && (
                  <div className="mt-3 pt-3 border-t border-dashed border-zinc-200">
                    <div className="text-xs font-semibold text-amber-900 mb-1">
                      Other appointments of the same type with no certificate tag ({candidates.length})
                    </div>
                    <div className="text-xs text-zinc-600 mb-2">
                      Acuity says all {cap.pack_size_from_name || "?"} sessions in this pack were redeemed but only {cap.matched_by_cert ?? 0} have the certificate tag set.
                      Pick the missing date(s) below to add to the receipt:
                    </div>
                    <div className="space-y-1">
                      {candidates.map((c) => {
                        const alreadyPicked = dates.includes(c.date);
                        return (
                          <div
                            key={c.id}
                            className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded bg-amber-50 border border-amber-100"
                            data-testid={`diag-candidate-${c.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <span className="font-mono-num text-zinc-900">{c.date}</span>
                              <span className="text-zinc-500"> · {c.type}</span>
                              {c.certificate && (
                                <span className="ml-1 text-rose-700">· tagged with {c.certificate}</span>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              disabled={alreadyPicked}
                              onClick={() => onAddDate(c.date)}
                              data-testid={`diag-candidate-add-${c.id}`}
                            >
                              {alreadyPicked ? "Added" : "+ Add"}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="text-xs text-zinc-500">
        If an expected date is missing, it means the appointment's <code className="font-mono-num">certificate</code> field
        in Acuity isn't set to one of the cert codes above. You can either add it here with the buttons above,
        or fix the appointment in Acuity by running the credit-apply job / setting the certificate manually.
      </div>
    </div>
  );
}



/**
 * ReceiptClientPicker — free-text input with a searchable dropdown of past
 * Acuity clients. On pick, the parent gets the full record so it can also
 * auto-fill email / phone / a line item from the client's most-recent
 * appointment type.
 */
function ReceiptClientPicker({ name, onNameChange, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounced search — keep the popover snappy without hammering the API.
  useEffect(() => {
    if (!q || q.trim().length < 2) { setRows([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/receipts/client-suggest", {
          params: { q: q.trim(), limit: 10 },
        });
        if (!cancelled) setRows(r.data?.clients || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div className="mt-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              value={name}
              onChange={(e) => {
                onNameChange(e.target.value);
                setQ(e.target.value);
                if (!open && e.target.value.trim().length >= 2) setOpen(true);
              }}
              onFocus={() => {
                if ((name || "").trim().length >= 2) {
                  setQ(name);
                  setOpen(true);
                }
              }}
              placeholder="Type to search past clients…"
              className="pl-9"
              data-testid="receipts-client-name"
            />
            {loading && (
              <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name, email or phone…"
              value={q}
              onValueChange={(v) => { setQ(v); onNameChange(v); }}
              className="h-9"
              data-testid="receipts-client-picker-search"
            />
            <CommandList className="max-h-72">
              {!loading && rows.length === 0 && (
                <CommandEmpty>
                  {q.trim().length < 2
                    ? "Type at least 2 characters."
                    : "No past clients match. You can still type the name manually."}
                </CommandEmpty>
              )}
              {rows.length > 0 && (
                <CommandGroup heading="Past clients">
                  {rows.map((c) => (
                    <CommandItem
                      key={c.email || c.name}
                      value={c.email || c.name}
                      onSelect={() => {
                        onPick(c);
                        setOpen(false);
                      }}
                      className="cursor-pointer flex flex-col items-start gap-0.5"
                      data-testid={`receipts-client-picker-row-${c.email || c.name}`}
                    >
                      <div className="flex items-baseline gap-2 w-full">
                        <span className="font-medium text-zinc-900 truncate">
                          {c.name || "—"}
                        </span>
                        <span className="ml-auto text-2xs font-mono-num text-zinc-500 tabular-nums shrink-0">
                          {c.appointment_count} appt{c.appointment_count === 1 ? "" : "s"}
                        </span>
                      </div>
                      <span className="text-2xs text-zinc-500 font-mono-num truncate w-full">
                        {c.email || "no email"}{c.phone ? ` · ${c.phone}` : ""}
                      </span>
                      {c.last_appointment_type && (
                        <span className="text-2xs text-zinc-600 truncate w-full">
                          Last: <span className="font-medium">{c.last_appointment_type}</span>
                          {c.suggested_line?.has_price ? (
                            <span className="ml-1 text-emerald-700 font-mono-num">
                              · {fmtMoney(c.suggested_line.rate)}
                            </span>
                          ) : null}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

